/**
 * Nachweise & Pflichten — the requirement read model and its override writes.
 *
 * THE READS DERIVE, THE WRITES STORE, matching `routes_admin_tenancy_requirements`.
 * The list GET derives every requirement of every tenancy as of the Swiss
 * business day, the timeline GET returns what was said about one of them, and
 * the reminder-preview GET returns what a letter would contain; the writes only
 * STORE decisions and derive nothing. Nothing in this module computes a status,
 * a deadline, a count or an eligibility — that would be a second engine beside
 * the backend's, free to disagree with it.
 *
 * THE GET RETURNS THE WHOLE ENVELOPE, deliberately, unlike the older domain
 * modules here that normalise to a bare `items` array. `summary` is a FACETED
 * count over the population before the status filter — the numbers the status
 * cards show — and `total` is the population after it. A helper that handed
 * back only `items` would leave the page to re-derive both from the current
 * page of rows, which is the one thing that cannot be done correctly.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  parseAdminErrorResponse,
} from "./core";

const BASE = "/api/admin/tenancy-requirements";

/** `tenancy_id × requirement_type` is the address — no row id exists until the
 *  first write, so both halves are path segments and both get encoded. */
function overrideUrl(tenancyId, requirementType, action) {
  return (
    `${API_BASE_URL}${BASE}/${encodeURIComponent(tenancyId)}` +
    `/${encodeURIComponent(requirementType)}/${action}`
  );
}

/**
 * Build the query string.
 *
 * Falsy values are OMITTED rather than sent as empty or `false`: the endpoint's
 * own defaults then apply, and — load-bearing — selecting the "Ausgesetzt"
 * facet must not smuggle an `include_suspended=true` the operator never asked
 * for. The backend already reaches suspended rows through `status=suspended`;
 * coupling the two here would silently widen every other card's population.
 *
 * `include_participant` IS coupled — to `tenant_id`, and deliberately. On its
 * own it has nothing to widen, and the route answers a bare
 * `include_participant=true` with a 400. Dropping it here when no tenant is
 * scoped keeps a caller's harmless-looking default from turning every
 * unscoped request into an error.
 */
export function buildTenancyRequirementsQuery(params = {}) {
  const sp = new URLSearchParams();
  if (params.status) sp.set("status", params.status);
  if (params.requirementType) sp.set("requirement_type", params.requirementType);
  if (params.search) sp.set("search", params.search);
  if (params.tenantId) sp.set("tenant_id", params.tenantId);
  if (params.tenantId && params.includeParticipant === true) {
    sp.set("include_participant", "true");
  }
  if (params.includeSuspended) sp.set("include_suspended", "true");
  if (params.skip != null) sp.set("skip", String(params.skip));
  if (params.limit != null) sp.set("limit", String(params.limit));
  return sp.toString();
}

export async function fetchAdminTenancyRequirements(params = {}) {
  const qs = buildTenancyRequirementsQuery(params);
  const res = await fetch(`${API_BASE_URL}${BASE}${qs ? `?${qs}` : ""}`, {
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  const data = await res.json();
  // An invalid shape throws rather than degrading to an empty list: a page that
  // renders "keine Nachweise" because the envelope was malformed is a lie the
  // operator has no way to detect.
  if (
    data == null ||
    typeof data !== "object" ||
    !Array.isArray(data.items) ||
    data.summary == null ||
    typeof data.summary !== "object"
  ) {
    throw new Error(
      'Ungültige Antwort von /api/admin/tenancy-requirements: erwartet { items, total, skip, limit, summary }.'
    );
  }
  return data;
}

/**
 * Set, change or remove the individual deadline.
 *
 * `payload` is forwarded VERBATIM because its meaning lives in which keys it
 * has: the route dumps the body with `exclude_unset`, so `{}` leaves the stored
 * deadline alone and `{ due_date_override: null }` removes it. Rebuilding the
 * object here — dropping nullish values, say — would erase that difference
 * before it ever reached the wire.
 */
export async function patchRequirementDueDate(tenancyId, requirementType, payload) {
  const res = await fetch(overrideUrl(tenancyId, requirementType, "due-date"), {
    method: "PATCH",
    headers: getApiHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/** Excuse this requirement. `payload.reason` is mandatory server-side. */
export async function waiveRequirement(tenancyId, requirementType, payload) {
  const res = await fetch(overrideUrl(tenancyId, requirementType, "waive"), {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/** Take the excuse back. No body — the route takes none. */
export async function unwaiveRequirement(tenancyId, requirementType) {
  const res = await fetch(overrideUrl(tenancyId, requirementType, "unwaive"), {
    method: "POST",
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/**
 * The contact history of ONE requirement (S8x-1).
 *
 * Same address as the override writes — `tenancy_id × requirement_type` — so it
 * goes through the same encoding. A separate `communicationUrl` rather than
 * reusing `overrideUrl` only because "communications" is a collection, not an
 * action performed on the requirement; they build the identical string and the
 * distinction is for the reader.
 */
function communicationUrl(tenancyId, requirementType) {
  return (
    `${API_BASE_URL}${BASE}/${encodeURIComponent(tenancyId)}` +
    `/${encodeURIComponent(requirementType)}/communications`
  );
}

/**
 * Read the timeline. `{ items, total }` — no paging, deliberately: the endpoint
 * is bounded server-side and the drawer shows the lot.
 *
 * The envelope is returned whole rather than flattened to `items`, matching
 * `fetchAdminTenancyRequirements` above: `total` is the backend's count, and a
 * caller that wanted it back from `items.length` would be right only until the
 * bound is reached.
 */
export async function fetchRequirementCommunications(tenancyId, requirementType) {
  const res = await fetch(communicationUrl(tenancyId, requirementType), {
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  const data = await res.json();
  if (data == null || typeof data !== "object" || !Array.isArray(data.items)) {
    throw new Error(
      "Ungültige Antwort von /communications: erwartet { items, total }."
    );
  }
  return data;
}

/**
 * Record one contact. Append-only — there is no update and no delete.
 *
 * THE BODY CARRIES FIVE KEYS AND NEVER SIX. `organization_id` comes from the
 * authenticated request and `actor_user_id` from the authenticated user; the
 * schema declares `extra="forbid"`, so sending either is a 422 rather than a
 * silently ignored field. Building the payload here key by key — instead of
 * spreading whatever the caller passed — is what keeps that promise at the one
 * place the request is actually assembled.
 *
 * Optional keys are OMITTED when empty rather than sent as `null`: `channel` is
 * legitimately absent on a note, and `occurred_at` absent means "the server's
 * own now", which is a better answer than a browser clock.
 */
export async function createRequirementCommunication(
  tenancyId,
  requirementType,
  input = {}
) {
  const body = { kind: input.kind };
  if (input.channel) body.channel = input.channel;
  if (input.occurred_at) body.occurred_at = input.occurred_at;
  if (input.subject) body.subject = input.subject;
  if (input.note) body.note = input.note;

  const res = await fetch(communicationUrl(tenancyId, requirementType), {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/**
 * What a reminder to this tenancy's tenant WOULD cover, right now (S9.2d).
 *
 * A READ, AND THE CONFIRMATION DIALOG DEPENDS ON THAT. It runs on every open
 * and may run again freely: the endpoint writes no delivery claim, no history
 * and calls no provider, so asking costs a real tenant nothing.
 *
 * `requirementType` IS THE ROW THE OPERATOR CLICKED, not a filter. One reminder
 * is one message per person, so the answer lists that person's whole
 * outstanding set — which is exactly the surprise the dialog exists to remove.
 * It travels because the URL is addressed that way, not because it narrows
 * anything.
 *
 * NOTHING HERE IS AUTHORITATIVE AND NOTHING IS SENT BACK. The send re-reads the
 * world a moment later and may legitimately cover a different set. That is why
 * the POST has no body: there is no version of this answer a client could
 * return that would not be an override of a decision the server has to make
 * again anyway.
 *
 * The shape is validated rather than trusted. A dialog that rendered an empty
 * list because the envelope was malformed would arm a send button beside the
 * sentence "nothing is outstanding" — the one combination that must not happen.
 *
 * @returns {Promise<{tenant_id: string, is_bundle: boolean, mail_item_count: number,
 *   items: Array<{requirement_type: string, text: string, tenancy_context: ?string}>,
 *   already_claimed_today: boolean, can_send: boolean, blocked_reason: ?string}>}
 */
export async function fetchRequirementReminderPreview(tenancyId, requirementType) {
  const res = await fetch(
    overrideUrl(tenancyId, requirementType, "reminder-preview"),
    { headers: getApiHeaders() }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  const data = await res.json();
  if (
    data == null ||
    typeof data !== "object" ||
    !Array.isArray(data.items) ||
    typeof data.can_send !== "boolean" ||
    typeof data.already_claimed_today !== "boolean"
  ) {
    throw new Error(
      "Ungültige Antwort von /reminder-preview: erwartet { items, can_send, already_claimed_today, … }."
    );
  }
  return data;
}

/**
 * The typed refusal of a send attempt (S9).
 *
 * WHY AN ERROR THAT CARRIES A BODY. Four of the seven outcomes come back on a
 * non-2xx status, and the caller has to tell them apart: "no address on file"
 * and "we do not know whether it went out" lead to opposite sentences, and one
 * of them must never be worded as a failure. The module's usual
 * `new Error(await parseAdminErrorResponse(res))` reduces all four to a string,
 * so this keeps `outcome` and `reason` attached — the same shape
 * `BexioDraftCreateError` uses for the same reason (`bexio.js:622-635`).
 */
export class RequirementReminderError extends Error {
  constructor(message, payload, status) {
    super(message);
    this.name = "RequirementReminderError";
    this.status = status;
    this.outcome = payload?.outcome ?? null;
    this.reason = payload?.reason ?? null;
  }
}

/**
 * Send ONE reminder about this requirement, now (S9).
 *
 * NO BODY AT ALL, and that is the contract rather than an omission. The
 * recipient is the tenancy's primary tenant, the wording is the server's
 * template, and the delivery identity's business day is the server's Zurich
 * today. There is no key a client could send that would not be an override of
 * one of those, so the request carries none — a stronger promise than a schema
 * that forbids them.
 *
 * NO RETRY, ever, and not even a transparent one: every POST can put a real
 * message in a real tenant's inbox. The server's delivery claim is what makes a
 * repeat safe, and a client-side retry would be asking it to work twice for no
 * reason.
 *
 * Parses the JSON body on BOTH paths, because the answer lives in the body for
 * 200 AND for 409/422/500/502 alike.
 *
 * @returns {Promise<{outcome: string, reason: ?string, message: string}>}
 * @throws {RequirementReminderError} on any non-2xx, with the body preserved
 */
export async function sendRequirementReminder(tenancyId, requirementType) {
  const res = await fetch(
    overrideUrl(tenancyId, requirementType, "send-reminder"),
    { method: "POST", headers: getApiHeaders() }
  );

  let payload = null;
  try {
    payload = await res.json();
  } catch (_) {
    payload = null;
  }

  if (!res.ok) {
    throw new RequirementReminderError(
      payload?.message || `HTTP ${res.status}`,
      payload,
      res.status
    );
  }
  return payload;
}
